/**
 * TG Handbook Page
 * Displays company handbook/guidelines with confirmation requirement
 */

import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import {
  BookOpen,
  Calendar,
  Clock,
  FileText,
  Briefcase,
  Wrench,
  Info,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
  Users,
  Settings
} from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { usePageTracking } from '../../hooks/usePageTracking'
import {
  getHandbookSections,
  needsHandbookConfirmation,
  confirmHandbook,
  getLatestHandbookVersion
} from '../../services/handbook'

// Icon mapping for sections
const sectionIcons: Record<string, React.ElementType> = {
  Calendar,
  Clock,
  FileText,
  Briefcase,
  Wrench,
  Info,
  BookOpen,
  Users,
  Settings
}

export function SubcontractorHandbook() {
  usePageTracking('handbook')

  const queryClient = useQueryClient()
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [subcontractorId, setSubcontractorId] = useState<string | null>(null)

  // Get subcontractor ID
  useEffect(() => {
    async function getSubcontractorId() {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data } = await supabase
          .from('subcontractors')
          .select('id')
          .eq('user_id', user.id)
          .single()
        if (data) {
          setSubcontractorId(data.id)
        }
      }
    }
    getSubcontractorId()
  }, [])

  // Fetch handbook sections
  const { data: sections = [], isLoading: sectionsLoading } = useQuery({
    queryKey: ['handbook-sections'],
    queryFn: getHandbookSections
  })

  // Check if confirmation is needed
  const { data: confirmationStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['handbook-confirmation-status', subcontractorId],
    queryFn: () => needsHandbookConfirmation(subcontractorId!),
    enabled: !!subcontractorId
  })

  // Get latest version for description
  const { data: latestVersion } = useQuery({
    queryKey: ['handbook-latest-version'],
    queryFn: getLatestHandbookVersion,
    enabled: confirmationStatus?.needsConfirmation === true
  })

  const handleConfirm = async () => {
    if (!subcontractorId || !confirmationStatus) return

    setConfirming(true)
    try {
      await confirmHandbook(subcontractorId, confirmationStatus.currentVersion)
      queryClient.invalidateQueries({ queryKey: ['handbook-confirmation-status'] })
      queryClient.invalidateQueries({ queryKey: ['handbook-needs-confirmation'] })
      toast.success('Tack! Du har bekräftat att du läst handboken.')
    } catch (error) {
      console.error('Error confirming handbook:', error)
      toast.error('Kunde inte bekräfta. Försök igen.')
    } finally {
      setConfirming(false)
    }
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId)
  }

  const getIconComponent = (iconName: string | null): React.ElementType => {
    if (iconName && sectionIcons[iconName]) {
      return sectionIcons[iconName]
    }
    return BookOpen
  }

  const isLoading = sectionsLoading || statusLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-nature-400" />
      </div>
    )
  }

  const sectionsWithContent = sections.filter(s => s.content?.content)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100 flex items-center space-x-3">
            <BookOpen className="h-7 w-7" />
            <span>Handbok</span>
          </h1>
          <p className="text-nature-600 dark:text-nature-400">
            Rutiner och riktlinjer för samarbetspartners till MB Trädgård Support AB
          </p>
        </div>
      </div>

      {/* Confirmation Banner */}
      {confirmationStatus?.needsConfirmation && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-900 dark:text-amber-100">
                {confirmationStatus.confirmedVersion
                  ? 'Handboken har uppdaterats'
                  : 'Bekräfta att du läst handboken'
                }
              </h3>
              {latestVersion?.description && confirmationStatus.confirmedVersion && (
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1 font-medium">
                  {latestVersion.description}
                </p>
              )}
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                {confirmationStatus.confirmedVersion
                  ? 'Läs igenom ändringarna nedan och bekräfta den nya versionen.'
                  : 'Läs igenom rutinerna nedan och bekräfta att du har tagit del av informationen.'
                }
              </p>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="mt-3 btn-primary text-sm flex items-center space-x-2"
              >
                {confirming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                <span>{confirming ? 'Bekräftar...' : 'Jag har läst och förstått'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmed Banner */}
      {confirmationStatus && !confirmationStatus.needsConfirmation && confirmationStatus.currentVersion > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div>
              <p className="font-medium text-green-900 dark:text-green-100">
                Du har bekräftat handboken
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Version {confirmationStatus.currentVersion}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sections */}
      {sectionsWithContent.length === 0 ? (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-8 text-center">
          <BookOpen className="h-12 w-12 text-nature-300 mx-auto mb-4" />
          <p className="text-nature-500 dark:text-nature-400">
            Handboken har inte fyllts i ännu.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sectionsWithContent.map((section) => {
            const IconComponent = getIconComponent(section.icon)
            const isExpanded = expandedSection === section.id
            const sectionColor = section.color || '#8B5CF6'

            return (
              <div
                key={section.id}
                className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${sectionColor}20`, color: sectionColor }}
                    >
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-nature-900 dark:text-nature-100">
                        {section.title}
                      </h3>
                      {section.description && (
                        <p className="text-sm text-nature-500 dark:text-nature-400">
                          {section.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-nature-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-nature-500" />
                  )}
                </button>

                {isExpanded && section.content && (
                  <div className="px-6 pb-6 border-t border-nature-200 dark:border-nature-700">
                    <div className="pt-4 prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown>{section.content.content}</ReactMarkdown>
                    </div>
                    {section.content.updated_at && (
                      <p className="text-xs text-nature-400 mt-4 pt-4 border-t border-nature-100 dark:border-nature-700">
                        Senast uppdaterad: {format(new Date(section.content.updated_at), 'yyyy-MM-dd', { locale: sv })}
                        {section.content.updated_by_name && ` av ${section.content.updated_by_name}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
